import service from "Utility/Service";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { AxiosRequestConfig } from "axios";

/* 用於從後端服務獲取設置信息。
   函數的參數包括 user（使用者名稱或身份）和 level（設置級別）。
   根據 level 不同，它會設置不同的 URL 路徑以獲取相應的設置信息。
   函數最終發送 HTTP GET 請求，獲取設置信息，然後返回該信息。
*/
export const getSetting = async (user: string | undefined, level: string) => {
  const url = level === "super_super" ? `api/supersetting/super_super/` : `api/usersetting/${user}/`; // FIXME: user@xxx.com -> user?

  const config: AxiosRequestConfig = {
    method: "get",
    url,
  };

  const { data } = await service(config);

  return data;
};
// 自訂義 Hook 獲取設置訊息，接收三個參數 user（使用者名稱或身份）、level（設置級別）和 isLogin（是否已登入）
// 調用此 Hook 會發送 HTTP GET 請求 獲得設置訊息 enabled ： isLogin   *若沒有登入，不自動發送請求*
export const useSetting = (user: string | undefined, level: string, isLogin: boolean) => {
  return useQuery(["setting"], () => getSetting(user, level), {
    // 沒有登入狀態的話 自動發動請求：false
    enabled: isLogin,
  });
};

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// const getUserSettingByName = async (user: string | undefined) => {
//   const config: AxiosRequestConfig = {
//     method: "get",
//     url: `api/usersetting/${user}/`,
//   };

//   const { data } = await service(config);

//   return data;
// };

// export const useUserSetting = (user: string | undefined, level: string, isLogin: boolean) => {
//   return useQuery(["userSetting"], () => getUserSettingByName(user), {
//     enabled: level !== "super_super" && isLogin,
//   });
// };

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// const getSuperSetting = async () => {
//   const config: AxiosRequestConfig = {
//     method: "get",
//     url: `api/supersetting/super_super/`,
//   };

//   const { data } = await service(config);

//   return data;
// };

// export const useSuperSetting = (level: string, isLogin: boolean) => {
//   return useQuery(["superSetting"], () => getSuperSetting(), {
//     enabled: level === "super_super" && isLogin,
//   });
// };

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface user_noti_props {
  user: string | undefined;
  notification_receive_time?: number;
  noti_list_receive_time?: number;
  isLock?: boolean;
  language?: string;
  ticket_language?: string;
  dark_mode?: boolean;
  time_zone?: number;
}

const updateUserSetting = async (props: user_noti_props) => {
  const {
    user,
    notification_receive_time,
    noti_list_receive_time,
    isLock,
    language,
    ticket_language,
    dark_mode,
    time_zone,
  } = props;
  const config: AxiosRequestConfig = {
    method: "patch",
    url: `api/usersetting/${user}/`,
    data: {},
  };

  if (notification_receive_time) {
    config.data.notification_receive_time = notification_receive_time;
  }
  if (noti_list_receive_time) {
    config.data.noti_list_receive_time = noti_list_receive_time;
  }
  if (isLock !== undefined) {
    config.data.isLock = isLock;
  }
  if (language) {
    config.data.language = language;
  }
  if (ticket_language) {
    config.data.ticket_language = ticket_language;
  }
  if (dark_mode !== undefined) {
    config.data.dark_mode = dark_mode;
  }
  if (time_zone) {
    config.data.time_zone = time_zone;
  }

  return service(config);
};

// 更新使用者設置 Hook 將新的設置信息 給後端進行更新。
// 若請求成功，則使用 queryClient.invalidateQueries 方法 ，將名為 setting 能即時加載為最新數據狀態。
export const useUpdateUserSetting = () => {
  const queryClient = useQueryClient();
  return useMutation((props: user_noti_props) => updateUserSetting(props), {
    onSuccess: () => queryClient.invalidateQueries(["setting"]),
  });
};

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface super_noti_props {
  notification_receive_time?: any;
  noti_list_receive_time?: any;
  isLock?: boolean;
  language?: string;
  ticket_language?: string;
  dark_mode?: boolean;
  time_zone?: number;
  timeout?: 300 | 600 | 900 | 1200;
  mail_server_active?: boolean;
  mail_server?: string;
  mail_port?: number;
  mail_from?: number;
  mail_from_alias?: string | number | null;
  mail_token?: number;
}

const updateSuperSetting = async (props: super_noti_props) => {
  const {
    notification_receive_time,
    noti_list_receive_time,
    isLock,
    language,
    ticket_language,
    dark_mode,
    time_zone,
    timeout,
    mail_server_active,
    mail_server,
    mail_port,
    mail_from,
    mail_from_alias,
    mail_token,
  } = props;

  const config: AxiosRequestConfig = {
    method: "patch",
    url: `api/supersetting/super_super/`,
    data: {},
  };
  if (notification_receive_time) {
    config.data.notification_receive_time = notification_receive_time;
  }
  if (noti_list_receive_time) {
    config.data.noti_list_receive_time = noti_list_receive_time;
  }
  if (isLock !== undefined) {
    config.data.isLock = isLock;
  }
  if (language) {
    config.data.language = language;
  }
  if (ticket_language) {
    config.data.ticket_language = ticket_language;
  }
  if (dark_mode !== undefined) {
    config.data.dark_mode = dark_mode;
  }
  if (time_zone) {
    config.data.time_zone = time_zone;
  }

  config.data.timeout = timeout;

  config.data.mail_server_active = mail_server_active;

  if (mail_server_active) {
    config.data.mail_server = mail_server;
    config.data.mail_port = mail_port;
    config.data.mail_from = mail_from;
    config.data.mail_from_alias = mail_from_alias;
    config.data.mail_token = mail_token;
  }

  return service(config);
};
// 更新超級使用者設置 Hook 將新的設置信息 給後端進行更新。
// 若請求成功，則使用 queryClient.invalidateQueries 方法 ，將名為 setting 能即時加載為最新數據狀態。
export const useUpdateSuperSetting = () => {
  const queryClient = useQueryClient();
  return useMutation((props: super_noti_props) => updateSuperSetting(props), {
    onSuccess: () => queryClient.invalidateQueries(["setting"]),
  });
};

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
